/**
 * RunListResult component
 * Displays a list of runs from tool results
 */
import { Link } from 'react-router-dom'
import { Folder, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { RunStatus } from '@/lib/types'

export interface RunListItem {
  /** Run ID */
  runId: string
  /** Current status */
  status: RunStatus
  /** Repository name */
  repoName: string
  /** Goal/task description */
  goal?: string
  /** When the run was created */
  createdAt: string
}

export interface RunListResultProps {
  /** List of runs */
  runs: RunListItem[]
  /** Whether data is loading */
  isLoading?: boolean
}

const statusColors: Record<string, string> = {
  SUCCEEDED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  RUNNING: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  PENDING: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  QUEUED: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  TIMEOUT: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
}

export function RunListResult({
  runs,
  isLoading = false,
}: RunListResultProps) {
  if (isLoading) {
    return (
      <div
        data-testid="run-list-loading"
        className="flex items-center gap-2 text-muted-foreground py-4"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading runs...</span>
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-4">
        No runs found
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">
        {runs.length} run{runs.length !== 1 ? 's' : ''}
      </div>
      <ul role="list" className="space-y-2">
        {runs.map((run) => (
          <li
            key={run.runId}
            role="listitem"
            className="rounded-md border bg-card p-3"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <Link
                to={`/runs/${run.runId}`}
                className="font-mono text-sm font-medium hover:underline truncate"
              >
                {run.runId}
              </Link>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                  statusColors[run.status] || statusColors.PENDING
                )}
              >
                {run.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1 mb-1">
              {run.goal || 'No description'}
            </p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Folder className="h-3 w-3" />
              <span>{run.repoName}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
